/**
 * Normalizes snackbar messages to consistent sentence case format
 * Converts messages to: First letter uppercase, rest lowercase, preserving punctuation
 * @param {string} message - The message to normalize
 * @returns {string} - Normalized message in sentence case
 */
export function normalizeSnackbarMessage(message) {
  if (!message || typeof message !== "string") {
    return "";
  }

  // Trim whitespace
  let normalized = message.trim();

  // If message is empty after trimming, return empty string
  if (!normalized) {
    return "";
  }

  // Remove multiple consecutive spaces
  normalized = normalized.replace(/\s+/g, " ");

  // Split by sentence boundaries (period, exclamation, question mark)
  // Regex matches: text that may or may not end with punctuation
  const sentences = normalized.match(/[^.!?]+[.!?]*|[.!?]+/g) || [normalized];

  // Normalize each sentence
  const normalizedSentences = sentences.map((sentence) => {
    // Trim the sentence
    let s = sentence.trim();

    // Skip empty sentences
    if (!s) return "";

    // Check if sentence ends with punctuation
    const endsWithPunctuation = /[.!?]$/.test(s);
    const punctuation = endsWithPunctuation ? s.slice(-1) : "";

    // Remove punctuation temporarily for processing
    if (endsWithPunctuation) {
      s = s.slice(0, -1).trim();
    }

    // Skip if nothing left after removing punctuation
    if (!s) return punctuation;

    // Convert to sentence case: first letter uppercase, rest lowercase
    if (s.length > 0) {
      s = s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    }

    // Reattach punctuation if it existed
    return s + punctuation;
  });

  // Join sentences with a space and remove any extra spaces
  return normalizedSentences
    .filter((s) => s)
    .join(" ")
    .trim();
}
